import { Controller } from "@hotwired/stimulus"
import { sourceCreditPlugin } from "./chart_controller"
import { Chart, registerables } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

const MOBILE_BREAKPOINT = 768

Chart.register(...registerables)

export default class extends Controller {
  static values = {
    data: Object,
    options: Object,
    lazyLoad: { type: Boolean, default: true }
  }

  connect() {
    this.chartInitialized = false
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT

    if (this.lazyLoadValue) {
      this.setupLazyLoading()
    } else {
      this.initializeChart()
    }

    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
    window.removeEventListener('resize', this.handleResize)
  }

  setupLazyLoading() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.chartInitialized) {
          this.initializeChart()
          this.chartInitialized = true
          this.observer.unobserve(this.element)
        }
      })
    }, options)

    this.observer.observe(this.element)
  }

  initializeChart() {
    const ctx = this.element.getContext('2d')

    if (this.chart) {
      this.chart.destroy()
    }

    const data = this.dataValue
    const options = this.buildOptions()

    const plugins = [sourceCreditPlugin]
    options.layout = options.layout || {}
    options.layout.padding = Object.assign({ bottom: 12 }, options.layout.padding || {})
    if (options.plugins?.datalabels?.display) {
      plugins.push(ChartDataLabels)
    }

    this.chart = new Chart(ctx, {
      type: 'scatter',
      data: data,
      options: options,
      plugins: plugins
    })
  }

  buildOptions() {
    const baseOptions = JSON.parse(JSON.stringify(this.optionsValue))

    // Apply tooltip label callback
    if (baseOptions.plugins?.tooltip?.callbacks) {
      baseOptions.plugins.tooltip.callbacks.label = this.tooltipLabelCallback
    }

    // Apply y-axis tick callback for percentage
    if (baseOptions.scales?.y?.ticks?.callback === 'percentage') {
      baseOptions.scales.y.ticks.callback = this.percentageTickCallback
    }

    // Apply datalabels formatter
    if (baseOptions.plugins?.datalabels?.formatter === 'label') {
      baseOptions.plugins.datalabels.formatter = this.datalabelsFormatter
    }

    // Handle mobile x-axis label wrapping
    this.applyMobileXAxisLabel(baseOptions)

    return baseOptions
  }

  tooltipLabelCallback(context) {
    return context.raw.label + ': (' + context.parsed.x + ', ' + context.parsed.y + '%)'
  }

  percentageTickCallback(value) {
    return value + '%'
  }

  datalabelsFormatter(value) {
    return value.label
  }

  applyMobileXAxisLabel(options) {
    if (!this.isMobile) return

    const xLabel = options.scales?.x?.title?.text
    if (xLabel && typeof xLabel === 'string' && xLabel.length > 20) {
      const words = xLabel.split(' ')
      const midPoint = Math.ceil(words.length / 2)
      options.scales.x.title.text = [
        words.slice(0, midPoint).join(' '),
        words.slice(midPoint).join(' ')
      ]
    }
  }

  handleResize() {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }

    this.resizeTimer = setTimeout(() => {
      const nowMobile = window.innerWidth < MOBILE_BREAKPOINT

      if (nowMobile !== this.isMobile) {
        this.isMobile = nowMobile

        if (this.chart && this.chartInitialized) {
          // Update options and re-render instead of page reload
          const newOptions = this.buildOptions()
          this.chart.options = newOptions
          this.chart.update()
        }
      }
    }, 250)
  }
}
